import * as React from 'react'
import { ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTitle,
    DialogTrigger,
    Switch,
    SwitchProps,
    Tooltip,
} from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import { FormikConfig } from 'formik/dist/types'
import { IFileDto } from '../../../../config/fileConfig'
import useSharePoint from '../../../../hooks/useSharePoint'
import { Destination, IGraphArrayPayload, IGraphPayload } from '../../../../context/AxiosContext'
import { MailFolder } from '@microsoft/microsoft-graph-types'
import { AxiosError, AxiosInstance } from 'axios'
import RootFolderCombobox, { RootFolderComboboxProps } from '../../../../components/Combobox/EmailFolder/RootFolderCombobox'
import useEmailFolders from '../../../../hooks/useEmailFolders'
import FormikField from '../../../../components/Formik/FormikField'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import ChildFolderCombobox from '../../../../components/Combobox/EmailFolder/ChildFolderCombobox'
import { ArrowDownRegular, ArrowUpRegular, LinkSquareRegular } from '@fluentui/react-icons'
import { Values } from '../../../../components/Dialog/LinkEmailFolder/LinkEmailFolderDialog'
import useAxios from '../../../../hooks/useAxios'
import useUser from '../../../../hooks/useUser'
import AuthorizationContext from '../../../../context/AuthorizationContext'

export const getLinkUserFolderDialogBodySchema = (axios: AxiosInstance, translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        create: Yup.boolean().required(),
        id: Yup.string().when('create', {
            is: false,
            then: schema => schema.required(translate('email.filing.synchronize.form.id.required')),
        }),
        parentFolderId: Yup.string().when('create', {
            is: true,
            then: schema => schema.required(translate('email.filing.synchronize.form.parentfolderid.required')),
        }),
        name: Yup.string().when('create', {
            is: true,
            then: schema =>
                schema.required(translate('email.filing.synchronize.form.name.required')).test({
                    name: 'folder-exists',
                    test: (value, { parent: { parentFolderId }, createError }) =>
                        parentFolderId &&
                        value &&
                        axios
                            .get<IGraphArrayPayload<MailFolder>>(`/mailFolders/${parentFolderId}/childFolders`, {
                                baseURL: Destination.Email,
                                headers: { SuppressErrors: 'true' },
                                params: {
                                    $filter: `displayName eq '${value}'`,
                                    includeHiddenFolders: true,
                                },
                            })
                            .then(response =>
                                response.data.value?.length === 0
                                    ? Promise.resolve(true)
                                    : Promise.reject(createError({ message: translate('email.filing.synchronize.form.name.exists') })),
                            )
                            .catch((error: AxiosError) => Promise.reject(createError(error))),
                }),
        }),
    })
}

export type LinkUserFolderDialogBodyProps = {
    file: IFileDto
    userEmail: string
    callback?: () => void
}

export type LinkUserFolderDialogBodyValues = {
    create: boolean
    id: string
    parentFolderId?: string
    name: string
    postAction?: string
}

const submitNew = (axios: AxiosInstance, values: Values): Promise<string> => {
    return axios
        .post<IGraphPayload<MailFolder>>(
            `/mailFolders/${values.parentFolderId}/childFolders`,
            { displayName: values.name },
            {
                baseURL: Destination.Email,
                headers: { SuppressErrors: 'true' },
            },
        )
        .then(response => response.data.id)
}

export default function LinkUserFolderDialogBody(
    props: HammockFormDialogProps<LinkUserFolderDialogBodyValues, LinkUserFolderDialogBodyProps>,
): ReactElement {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const [rootFolderId, folders] = useEmailFolders()
    const { sharePoint } = useSharePoint()
    const { t: translate } = useTranslation()
    const { currentMailbox } = useUser()
    const { viewport, isViewport } = useViewport()

    const { values, setFieldValue, isSubmitting, setErrors, submitForm } = useFormikContext<LinkUserFolderDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps() ?? ({} as LinkUserFolderDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const handleSubmit = useCallback<FormikConfig<LinkUserFolderDialogBodyValues>['onSubmit']>(
        values => {
            return (values.create ? submitNew(axios, values) : Promise.resolve(values.id)).then(folderId => {
                axios
                    .post<void>(
                        `/email/${currentMailbox}/folder`,
                        { id: folderId, fileId: dialogFormBodyProps.file.id, enabled: true },
                        { headers: { SuppressErrors: 'true' } },
                    )
                    .then(() => {
                        if (values.postAction === 'export') {
                            axios.post(
                                '/api/synchronise-file/',
                                { hostKey: currentHost.RowKey, reference: dialogFormBodyProps.file.reference, userEmail: currentMailbox },
                                {
                                    baseURL: process.env.INBOXER_HOST,
                                    headers: { SuppressErrors: 'true' },
                                },
                            )
                        } else if (values.postAction === 'import') {
                            axios.post(
                                `/api/import-folder/`,
                                {
                                    hostKey: currentHost.RowKey,
                                    reference: dialogFormBodyProps.file.reference,
                                    userEmail: currentMailbox,
                                    folderId: values.id,
                                },
                                {
                                    baseURL: process.env.INBOXER_HOST,
                                    headers: { SuppressErrors: 'true' },
                                },
                            )
                        }
                    })
            })
        },
        [sharePoint],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{translate(values.create ? 'email.filing.synchronize.subtitle' : 'email.filing.synchronize.title')}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormikField<SwitchProps, LinkUserFolderDialogBodyValues>
                        required
                        labelKey={'email.filing.synchronize.subtitle'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'create'}
                        component={Switch}
                        componentProps={{
                            onChange: (e, data) => {
                                setErrors(undefined)
                                setFieldValue('create', data.checked)
                            },
                        }}
                    />
                    {values.create ? (
                        <>
                            <FormikComboboxField<LinkUserFolderDialogBodyValues, RootFolderComboboxProps>
                                required
                                labelKey={'email.filing.synchronize.form.parentfolderid'}
                                orientation={viewportSmall ? 'vertical' : 'horizontal'}
                                fieldName={'parentFolderId'}
                                component={RootFolderCombobox}
                                componentProps={{
                                    rootFolderId: rootFolderId,
                                    folders: folders,
                                }}
                            />
                            <FormikInputField<LinkUserFolderDialogBodyValues>
                                required
                                labelKey={'email.filing.synchronize.form.name'}
                                orientation={viewportSmall ? 'vertical' : 'horizontal'}
                                fieldName={'name'}
                            />
                        </>
                    ) : (
                        <FormikComboboxField<LinkUserFolderDialogBodyValues, RootFolderComboboxProps>
                            required
                            labelKey={'email.filing.synchronize.form.id'}
                            orientation={viewportSmall ? 'vertical' : 'horizontal'}
                            fieldName={'id'}
                            component={ChildFolderCombobox}
                            componentProps={{
                                isSelectableGroup: () => true,
                                rootFolderId: rootFolderId,
                                folders: folders,
                            }}
                        />
                    )}
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Tooltip content={translate('email.filing.synchronize.link.tooltip')} relationship="description" withArrow>
                    <Button appearance="primary" icon={<LinkSquareRegular />} type="submit">
                        {translate('email.filing.synchronize.link')}
                    </Button>
                </Tooltip>
                {!values.create && (
                    <Tooltip content={translate('email.filing.synchronize.import.tooltip')} relationship="description" withArrow>
                        <Button
                            appearance="primary"
                            icon={<ArrowUpRegular />}
                            onClick={() => setFieldValue('postAction', 'import').then(() => submitForm())}
                        >
                            {translate('email.filing.synchronize.import')}
                        </Button>
                    </Tooltip>
                )}
                <Tooltip content={translate('email.filing.synchronize.export.tooltip')} relationship="description" withArrow>
                    <Button
                        appearance="primary"
                        icon={<ArrowDownRegular />}
                        onClick={() => setFieldValue('postAction', 'export').then(() => submitForm())}
                    >
                        {translate('email.filing.synchronize.export')}
                    </Button>
                </Tooltip>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" disabled={isSubmitting}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
